import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Stack,
  Divider,
  Collapse,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ReplayIcon from '@mui/icons-material/Replay';
import AddIcon from '@mui/icons-material/Add';
import PostAddOutlinedIcon from '@mui/icons-material/PostAddOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { toast } from 'react-toastify';

import { fetchHrRequests, downloadPreApprovalPdf, updateRequestStatus } from '../../services/allRequestsApi';
import { canAuthorPreApprovalRequest, getLocalRole } from '../AllRequests/components/requestsUiUtils';

/* ---------------------------------------------------------------------------
 * Pre-Approval tracking + light approval board.
 *
 * Pre-approvals are HrRequest rows with request_type = 'pre_approval'. We pull
 * the user's submitted ones (my_requests), everything routed to them
 * (assigned_requests/all) for tracking, and specifically the pending-assigned
 * set (assigned_requests/pending) — those are the requests it is the user's
 * turn to action, so they get inline Approve / Revise / Reject controls. The
 * backend re-validates authority on every status change.
 *
 * HR-team / management authors also get a "New Pre-Approval" button.
 * ------------------------------------------------------------------------- */

const STATUS_BUCKETS = [
  { value: 'all', label: 'All statuses' },
  { value: 'awaiting', label: 'Awaiting my action' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'revision', label: 'Needs revision' },
];

const BILLING_OPTIONS = [
  { value: 'billed', label: 'Billed' },
  { value: 'lumpsum', label: 'Lumpsum' },
  { value: 'billing_hq', label: 'Billing HQ' },
];

function statusBucket(raw) {
  const s = String(raw || '').toLowerCase();
  if (s === 'fully_approved' || s === 'approved') return 'approved';
  if (s === 'rejected') return 'rejected';
  if (s.includes('revision') || s.includes('revise')) return 'revision';
  return 'in_progress';
}

const BUCKET_META = {
  approved: { label: 'Approved', color: 'success' },
  rejected: { label: 'Rejected', color: 'error' },
  revision: { label: 'Needs revision', color: 'warning' },
  in_progress: { label: 'In progress', color: 'info' },
};

function stepMeta(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'approved') return { label: 'Approved', color: 'success', Icon: CheckCircleIcon };
  if (s === 'rejected') return { label: 'Rejected', color: 'error', Icon: CancelIcon };
  if (s === 'pending') return { label: 'Pending', color: 'info', Icon: HourglassEmptyIcon };
  if (s.includes('revision') || s.includes('revise')) return { label: 'Revision', color: 'warning', Icon: HourglassEmptyIcon };
  return { label: 'Not started', color: 'default', Icon: RadioButtonUncheckedIcon };
}

function humanize(value) {
  const s = String(value || '').replace(/_/g, ' ').trim();
  if (!s) return '—';
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function formatPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || value === null || value === '' || value === undefined) return '—';
  return `${n.toFixed(2)}%`;
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

// Open the approved pre-approval PDF in a new tab. Shared by the desktop table
// row and the small-screen card so the behaviour stays identical.
async function openPreApprovalPdf(row) {
  const win = window.open('', '_blank');
  try {
    const blob = await downloadPreApprovalPdf(row.id, false);
    const url = window.URL.createObjectURL(blob);
    if (win) win.location.href = url;
    else window.open(url, '_blank', 'noopener');
    window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  } catch (err) {
    if (win) win.close();
    console.error('Failed to open pre-approval PDF:', err);
    toast.error('The PDF becomes available once the request is fully approved.');
  }
}

// Map an approved pre-approval onto the job-offer form fields. Only clearly
// equivalent fields are carried over; empty/zero values are dropped so the
// job-offer form keeps its own sensible defaults, and the recruiter completes
// the candidate-specific and financial-detail fields.
function buildJobOfferPrefill(row) {
  const money = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? String(n) : '';
  };
  const mapped = {
    candidate_name: row.candidate_name || '',
    nationality: row.nationality || '',
    position: row.position_offered || '',
    project_name: row.project_client || '',
    proposed_total_package: money(row.package_salary),
    budgeted_total_package: money(row.total_cost_to_company),
    monthly_billing_rate: money(row.client_billing),
    conditional_terms: row.conditional_terms || '',
    notes: `Created from pre-approval ${row.pre_approval_reference || `#${row.id}`}.`,
  };
  return Object.fromEntries(Object.entries(mapped).filter(([, v]) => v !== '' && v != null));
}

// Compact labelled metric used in the small-screen card's financial strip.
function Metric({ label, value }) {
  return (
    <Box sx={{ minWidth: 0, textAlign: 'center' }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', fontWeight: 700, lineHeight: 1.2, letterSpacing: 0.2 }}
        noWrap
      >
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={800} noWrap>
        {value}
      </Typography>
    </Box>
  );
}

// Mirror of ShowHrRequestPage.getWorkflowActionPayload — include the pending
// workflow task id when the request is driven by the workflow engine.
function workflowActionPayload(r) {
  const rows = [];
  if (Array.isArray(r?.workflow_task_metadata)) rows.push(...r.workflow_task_metadata);
  if (Array.isArray(r?.workflow_tasks)) rows.push(...r.workflow_tasks);
  if (Array.isArray(r?.workflowTasks)) rows.push(...r.workflowTasks);
  if (Array.isArray(r?.workflow_instance?.tasks)) rows.push(...r.workflow_instance.tasks);
  if (Array.isArray(r?.workflowInstance?.tasks)) rows.push(...r.workflowInstance.tasks);
  const task = rows.find((t) => String(t?.status || '').toLowerCase() === 'pending' && (t?.task_id || t?.id));
  const taskId = task?.task_id || task?.id;
  return taskId ? { workflow_task_id: taskId } : {};
}

function buildSteps(r) {
  const steps = [];

  // 1) HR Admin Review — the first pre-approval step. It uses the shared
  //    `hr_status` column (NOT a pre_approval_* field), which is why it has to
  //    be handled separately. Shown whenever the request is routed through HR.
  if (String(r.hr_status || '').trim() !== '') {
    steps.push({
      label: 'HR Manager',
      approver: r.hr_action_by_name || null,
      status: r.hr_status,
      actionBy: null,
      actionAt: r.hr_action_at || null,
      reason: r.hr_rejection_reason || null,
    });
  }

  // 2) Management chain — each uses its own pre_approval_* fields.
  const defs = [
    { key: 'hod', label: 'HOD', skip: !!r.pre_approval_skip_hod },
    { key: 'operations', label: 'Operations' },
    { key: 'ceo', label: 'CEO' },
    { key: 'executive_md', label: 'Executive MD' },
  ];
  defs
    .filter((d) => !d.skip)
    .forEach((d) => {
      const step = {
        label: d.label,
        approver: r[`pre_approval_${d.key}_name`],
        status: r[`pre_approval_${d.key}_status`],
        actionBy: r[`pre_approval_${d.key}_action_by_name`],
        actionAt: r[`pre_approval_${d.key}_action_at`],
        reason: r[`pre_approval_${d.key}_rejection_reason`],
      };
      if (step.approver || step.status) steps.push(step);
    });

  return steps;
}

function WorkflowChain({ row }) {
  const steps = buildSteps(row);
  const history = Array.isArray(row.preApprovalRevisionHistory) ? row.preApprovalRevisionHistory : [];

  if (steps.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        No approval chain recorded for this request.
      </Typography>
    );
  }

  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
        Approval workflow
      </Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1.5 }}>
        {steps.map((step, idx) => {
          const meta = stepMeta(step.status);
          const Icon = meta.Icon;
          return (
            <Card
              key={`${step.label}-${idx}`}
              variant="outlined"
              sx={{ borderRadius: 2, minWidth: 200, flex: 1, borderColor: `${meta.color}.light` }}
            >
              <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Typography variant="caption" fontWeight={800} color="text.secondary">
                    {idx + 1}. {step.label}
                  </Typography>
                  <Chip size="small" color={meta.color} icon={<Icon sx={{ fontSize: 15 }} />} label={meta.label} sx={{ fontWeight: 700, height: 22 }} />
                </Stack>
                <Typography variant="body2" fontWeight={700} sx={{ mt: 0.5 }}>
                  {step.approver || '—'}
                </Typography>
                {step.actionBy && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    by {step.actionBy} · {formatDate(step.actionAt)}
                  </Typography>
                )}
                {step.reason && (
                  <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5 }}>
                    Reason: {step.reason}
                  </Typography>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      {history.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>
            Revision history
          </Typography>
          <Stack spacing={0.5}>
            {history.map((h, i) => (
              <Typography key={h.id || i} variant="caption" color="text.secondary">
                • {h.requested_by_label || humanize(h.requested_by_step)} requested a change
                {h.target_label ? ` to ${h.target_label}` : ''}
                {h.requested_by_name ? ` (${h.requested_by_name})` : ''}
                {h.reason ? ` — "${h.reason}"` : ''}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

function PreApprovalRow({ row, onAct, canCreateJobOffer }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const bucket = statusBucket(row.pre_approval_status);
  const meta = BUCKET_META[bucket];
  const canDownloadPdf = bucket === 'approved';
  const actionable = !!row._actionable;
  const goCreateJobOffer = () =>
    navigate('/hr/job-offers/new', {
      state: {
        jobOfferPrefill: buildJobOfferPrefill(row),
        fromPreApprovalRef: row.pre_approval_reference || `#${row.id}`,
      },
    });

  return (
    <>
      <TableRow hover sx={actionable ? { '& > td': { bgcolor: 'warning.50' } } : undefined}>
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={() => setOpen((o) => !o)} aria-label="toggle workflow">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
          {row.pre_approval_reference || `#${row.id}`}
        </TableCell>
        <TableCell>{row.candidate_name || '—'}</TableCell>
        <TableCell>{row.position_offered || '—'}</TableCell>
        <TableCell>{row.pre_approval_department_name || '—'}</TableCell>
        <TableCell align="right">{formatMoney(row.total_cost_to_company)}</TableCell>
        <TableCell align="right">{formatMoney(row.client_billing)}</TableCell>
        <TableCell align="right">{formatPercent(row.marginal_benefit)}</TableCell>
        <TableCell>{row.pre_approval_billing_mode_display || humanize(row.pre_approval_billing_mode)}</TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title={humanize(row.pre_approval_status)}>
              <Chip size="small" color={meta.color} label={meta.label} sx={{ fontWeight: 700 }} />
            </Tooltip>
            {actionable && <Chip size="small" color="warning" variant="outlined" label="Your action" sx={{ fontWeight: 700 }} />}
          </Stack>
        </TableCell>
        <TableCell>{formatDate(row.created_at)}</TableCell>
        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
          <Stack direction="row" spacing={0.25} justifyContent="center">
            <Tooltip title="View details">
              <IconButton size="small" onClick={() => navigate(`/hr-requests/${row.id}`)}>
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {actionable ? (
              <>
                <Tooltip title="Approve">
                  <IconButton size="small" color="success" onClick={() => onAct(row, 'approved')}>
                    <CheckCircleIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Request revision">
                  <IconButton size="small" color="warning" onClick={() => onAct(row, 'revised')}>
                    <ReplayIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reject">
                  <IconButton size="small" color="error" onClick={() => onAct(row, 'rejected')}>
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            ) : canDownloadPdf ? (
              <>
                <Tooltip title="Open PDF">
                  <IconButton size="small" color="primary" onClick={() => openPreApprovalPdf(row)}>
                    <PictureAsPdfOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {canCreateJobOffer && (
                  <Tooltip title="Create job offer from this pre-approval">
                    <IconButton size="small" color="secondary" onClick={goCreateJobOffer}>
                      <PostAddOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            ) : (
              <Tooltip title="PDF available after final approval">
                <span>
                  <IconButton size="small" disabled>
                    <PictureAsPdfOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={12} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ px: 2, pb: 1.5, bgcolor: 'action.hover', borderRadius: 1, my: 1 }}>
              <WorkflowChain row={row} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// Small-screen / laptop card. Same data + actions as a table row, laid out
// vertically so it never overflows horizontally on narrow viewports.
function PreApprovalCard({ row, onAct, canCreateJobOffer }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const bucket = statusBucket(row.pre_approval_status);
  const meta = BUCKET_META[bucket];
  const canDownloadPdf = bucket === 'approved';
  const actionable = !!row._actionable;
  const goCreateJobOffer = () =>
    navigate('/hr/job-offers/new', {
      state: {
        jobOfferPrefill: buildJobOfferPrefill(row),
        fromPreApprovalRef: row.pre_approval_reference || `#${row.id}`,
      },
    });

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: actionable ? 'warning.main' : 'divider',
        bgcolor: actionable ? 'warning.50' : 'background.paper',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 }, display: 'flex', flexDirection: 'column', flex: 1 }}>
        {/* Header — reference + date / status chips */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontFamily: 'monospace', fontWeight: 800 }} noWrap>
              {row.pre_approval_reference || `#${row.id}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDate(row.created_at)}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end" flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
            <Chip size="small" color={meta.color} label={meta.label} sx={{ fontWeight: 700 }} />
            {actionable && <Chip size="small" color="warning" variant="outlined" label="Your action" sx={{ fontWeight: 700 }} />}
          </Stack>
        </Stack>

        {/* Candidate / position / department */}
        <Box sx={{ mt: 1 }}>
          <Typography variant="body1" fontWeight={800} noWrap>
            {row.candidate_name || '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {row.position_offered || '—'}
            {row.pre_approval_department_name ? ` · ${row.pre_approval_department_name}` : ''}
          </Typography>
        </Box>

        {/* Financial strip */}
        <Box
          sx={{
            mt: 1.25,
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 1,
            bgcolor: 'action.hover',
            borderRadius: 2,
            p: 1,
          }}
        >
          <Metric label="Total Cost" value={formatMoney(row.total_cost_to_company)} />
          <Metric label="Client Billing" value={formatMoney(row.client_billing)} />
          <Metric label="Margin %" value={formatPercent(row.marginal_benefit)} />
        </Box>

        {/* Billing mode */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            Billing
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            label={row.pre_approval_billing_mode_display || humanize(row.pre_approval_billing_mode)}
            sx={{ fontWeight: 700 }}
          />
        </Stack>

        <Divider sx={{ my: 1.25 }} />

        {/* Actions */}
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 'auto' }}>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => navigate(`/hr-requests/${row.id}`)}
          >
            View
          </Button>
          {actionable ? (
            <>
              <Button size="small" variant="contained" color="success" startIcon={<CheckCircleIcon />} onClick={() => onAct(row, 'approved')}>
                Approve
              </Button>
              <Button size="small" variant="outlined" color="warning" startIcon={<ReplayIcon />} onClick={() => onAct(row, 'revised')}>
                Revise
              </Button>
              <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon />} onClick={() => onAct(row, 'rejected')}>
                Reject
              </Button>
            </>
          ) : canDownloadPdf ? (
            <>
              <Button size="small" variant="outlined" color="primary" startIcon={<PictureAsPdfOutlinedIcon />} onClick={() => openPreApprovalPdf(row)}>
                PDF
              </Button>
              {canCreateJobOffer && (
                <Button size="small" variant="contained" color="secondary" startIcon={<PostAddOutlinedIcon />} onClick={goCreateJobOffer}>
                  Create Job Offer
                </Button>
              )}
            </>
          ) : null}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            size="small"
            color="inherit"
            onClick={() => setOpen((o) => !o)}
            endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            aria-label="toggle workflow"
          >
            Workflow
          </Button>
        </Stack>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 1, px: 0.5 }}>
            <WorkflowChain row={row} />
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

const emptyDialog = {
  open: false,
  action: 'approved',
  row: null,
  reason: '',
  revisionTarget: 'hr_team_creator',
  billingMode: '',
  clientBilling: '',
  margin: '',
};

export default function PreApprovalTrackingPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  // Laptops and anything narrower get the vertical card layout; the wide
  // 12-column table is reserved for large external monitors (xl+) where it
  // fits without horizontal scrolling.
  const isCompact = useMediaQuery(theme.breakpoints.down('xl'));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialog, setDialog] = useState(emptyDialog);
  const [submitting, setSubmitting] = useState(false);

  const canCreate = useMemo(() => canAuthorPreApprovalRequest(getLocalRole()), []);

  const load = async () => {
    setLoading(true);
    try {
      // 'all' = company-wide read-only set (backend grants it to HR-team /
      // recruiters only; everyone else gets an empty list and falls back to the
      // my/assigned scopes below). 'assigned_requests'+pending marks the rows it
      // is the current user's turn to action.
      const [allRows, mine, assignedAll, assignedPending] = await Promise.all([
        fetchHrRequests({ request_type: 'pre_approval', view_mode: 'all', queue: 'all' }).catch(() => []),
        fetchHrRequests({ request_type: 'pre_approval', view_mode: 'my_requests', queue: 'all' }).catch(() => []),
        fetchHrRequests({ request_type: 'pre_approval', view_mode: 'assigned_requests', queue: 'all' }).catch(() => []),
        fetchHrRequests({ request_type: 'pre_approval', view_mode: 'assigned_requests', queue: 'pending' }).catch(() => []),
      ]);

      const actionableIds = new Set((Array.isArray(assignedPending) ? assignedPending : []).map((r) => r.id));
      const byId = new Map();
      [
        ...(Array.isArray(allRows) ? allRows : []),
        ...(Array.isArray(mine) ? mine : []),
        ...(Array.isArray(assignedAll) ? assignedAll : []),
        ...(Array.isArray(assignedPending) ? assignedPending : []),
      ].forEach((r) => {
        if (r && r.id != null && !byId.has(r.id)) byId.set(r.id, { ...r, _actionable: actionableIds.has(r.id) });
      });
      const merged = Array.from(byId.values()).sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      setRows(merged);
    } catch (err) {
      console.error('Failed to load pre-approval requests:', err);
      toast.error('Could not load pre-approval requests.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const counts = useMemo(() => {
    const c = { total: rows.length, awaiting: 0, approved: 0, rejected: 0 };
    rows.forEach((r) => {
      if (r._actionable) c.awaiting += 1;
      const b = statusBucket(r.pre_approval_status);
      if (b === 'approved') c.approved += 1;
      if (b === 'rejected') c.rejected += 1;
    });
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter === 'awaiting' && !r._actionable) return false;
      if (statusFilter !== 'all' && statusFilter !== 'awaiting' && statusBucket(r.pre_approval_status) !== statusFilter) return false;
      if (!term) return true;
      const haystack = [r.pre_approval_reference, r.candidate_name, r.position_offered, r.project_client, r.pre_approval_department_name]
        .map((x) => String(x || '').toLowerCase())
        .join(' ');
      return haystack.includes(term);
    });
  }, [rows, search, statusFilter]);

  const openDialog = (row, action) => {
    setDialog({
      ...emptyDialog,
      open: true,
      action,
      row,
      revisionTarget: 'hr_team_creator',
      billingMode: String(row.pre_approval_billing_mode || '').trim(),
      clientBilling: row.client_billing != null ? String(row.client_billing) : '',
      margin: row.marginal_benefit != null ? String(row.marginal_benefit) : '',
    });
  };
  const closeDialog = () => setDialog(emptyDialog);

  const row = dialog.row;
  const currentStep = String(row?.pre_approval_current_step || '').toLowerCase();
  // Commercial (billing) capture happens at the HOD step — or at the Operations
  // step when HOD is skipped for this request. Mirrors the backend's
  // shouldCapturePreApprovalCommercialApproval().
  const isCommercial =
    dialog.action === 'approved' &&
    (currentStep === 'hod' || (currentStep === 'operations_manager' && !!row?.pre_approval_skip_hod));
  const revisionTargetOptions =
    ['operations_manager', 'ceo', 'executive_managing_director'].includes(currentStep)
      ? [
          { value: 'hr_team_creator', label: 'HR Team Creator' },
          { value: 'hod', label: 'Head of Department' },
        ]
      : [{ value: 'hr_team_creator', label: 'HR Team Creator' }];

  // Auto-calculated margin preview for "billed" mode.
  const billedMarginPreview = useMemo(() => {
    if (!isCommercial || dialog.billingMode !== 'billed') return null;
    const totalCost = Number(row?.total_cost_to_company) || 0;
    const billing = Number(dialog.clientBilling) || 0;
    if (billing <= 0) return null;
    return Math.round((1 - totalCost / billing) * 10000) / 100;
  }, [isCommercial, dialog.billingMode, dialog.clientBilling, row]);

  const submitDialog = async () => {
    if (!row) return;
    const payload = { ...workflowActionPayload(row) };
    const reason = String(dialog.reason || '').trim();

    if (dialog.action === 'rejected') {
      if (!reason) { toast.error('Rejection reason is required.'); return; }
      payload.rejection_reason = reason;
    }

    if (dialog.action === 'revised') {
      if (!dialog.revisionTarget) { toast.error('Revision target is required.'); return; }
      if (!reason) { toast.error('Revision reason is required.'); return; }
      payload.revision_target_step = dialog.revisionTarget;
      payload.revision_reason = reason;
    }

    if (isCommercial) {
      const mode = dialog.billingMode;
      if (!mode) { toast.error('Billing mode is required.'); return; }
      payload.pre_approval_billing_mode = mode;
      if (mode === 'billing_hq') {
        // backend nulls client_billing + margin for HQ billing
      } else {
        const billing = Number(dialog.clientBilling);
        if (!(billing > 0)) { toast.error('Client billing must be greater than 0.'); return; }
        payload.client_billing = billing;
        if (mode === 'lumpsum') {
          const m = Number(dialog.margin);
          if (!Number.isFinite(m)) { toast.error('Margin benefit % is required for lumpsum.'); return; }
          payload.marginal_benefit = m;
        } else {
          // billed → auto-calculated
          payload.marginal_benefit = billedMarginPreview ?? 0;
        }
      }
    }

    setSubmitting(true);
    try {
      await updateRequestStatus(row.id, dialog.action, payload);
      const verb = dialog.action === 'approved' ? 'approved' : dialog.action === 'rejected' ? 'rejected' : 'sent back for revision';
      toast.success(`Request ${verb}.`);
      closeDialog();
      await load();
      if (typeof window.refreshAssignedPendingCount === 'function') window.refreshAssignedPendingCount();
    } catch (err) {
      console.error('Pre-approval action failed:', err);
      const msg = err?.response?.data?.message || 'Action failed. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const dialogTitle =
    dialog.action === 'approved' ? 'Approve pre-approval' : dialog.action === 'rejected' ? 'Reject pre-approval' : 'Request revision';

  const summaryCards = [
    { key: 'total', label: 'Total', value: counts.total, color: 'primary.main' },
    { key: 'awaiting', label: 'Awaiting my action', value: counts.awaiting, color: 'warning.main' },
    { key: 'approved', label: 'Approved', value: counts.approved, color: 'success.main' },
    { key: 'rejected', label: 'Rejected', value: counts.rejected, color: 'error.main' },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FactCheckOutlinedIcon color="primary" />
            <Typography variant="h5" fontWeight={800}>
              Pre-Approval Tracking
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Track pre-approval requests across the workflow. Rows awaiting your action can be approved, revised or rejected here.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {canCreate && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/hr-requests/new?type=pre_approval')}>
              New Pre-Approval
            </Button>
          )}
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={load} disabled={loading} color="primary">
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {summaryCards.map((card) => (
          <Grid size={{ xs: 6, md: 3 }} key={card.key}>
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase">
                  {card.label}
                </Typography>
                <Typography variant="h4" fontWeight={800} sx={{ color: card.color }}>
                  {card.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
          <TextField
            fullWidth
            size="small"
            placeholder="Search reference, candidate, position, department…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
          />
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 220 } }}
          >
            {STATUS_BUCKETS.map((b) => (
              <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>

      {/* Results — dense table on xl+, vertical cards on laptop / small screens */}
      {loading ? (
        <Paper variant="outlined" sx={{ borderRadius: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        </Paper>
      ) : filtered.length === 0 ? (
        <Paper variant="outlined" sx={{ borderRadius: 3 }}>
          <Box sx={{ textAlign: 'center', py: 8, px: 2 }}>
            <FactCheckOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="subtitle1" fontWeight={700} color="text.secondary">
              No pre-approval requests found
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {rows.length === 0 ? 'There are no pre-approval requests linked to your account yet.' : 'No requests match your current filters.'}
            </Typography>
          </Box>
        </Paper>
      ) : isCompact ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 1.5,
            alignItems: 'start',
          }}
        >
          {filtered.map((r) => (
            <PreApprovalCard key={r.id} row={r} onAct={openDialog} canCreateJobOffer={canCreate} />
          ))}
        </Box>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: '64vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell sx={{ fontWeight: 800 }}>Reference</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Candidate</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Position</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Department</TableCell>
                  <TableCell sx={{ fontWeight: 800 }} align="right">Total Cost</TableCell>
                  <TableCell sx={{ fontWeight: 800 }} align="right">Client Billing</TableCell>
                  <TableCell sx={{ fontWeight: 800 }} align="right">Margin %</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Billing</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Submitted</TableCell>
                  <TableCell sx={{ fontWeight: 800 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((r) => (
                  <PreApprovalRow key={r.id} row={r} onAct={openDialog} canCreateJobOffer={canCreate} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Divider sx={{ mt: 2 }} />
      <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
        Showing {filtered.length} of {rows.length} pre-approval request(s). PDF export is available after final approval.
      </Typography>

      {/* Action dialog */}
      <Dialog open={dialog.open} onClose={submitting ? undefined : closeDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800 }}>{dialogTitle}</DialogTitle>
        <DialogContent dividers>
          {row && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {row.pre_approval_reference || `#${row.id}`} · {row.candidate_name || '—'} · {row.position_offered || '—'}
            </Typography>
          )}

          {dialog.action === 'approved' && !isCommercial && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Confirm approval of this pre-approval at the current step.
            </Alert>
          )}

          {isCommercial && (
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <Alert severity="info">Commercial approval — set the billing terms for this engagement.</Alert>
              <TextField
                select
                label="Billing mode"
                value={dialog.billingMode}
                onChange={(e) => setDialog((d) => ({ ...d, billingMode: e.target.value }))}
                fullWidth
                required
              >
                {BILLING_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
              {dialog.billingMode && dialog.billingMode !== 'billing_hq' && (
                <TextField
                  label="Client billing (no VAT)"
                  type="number"
                  value={dialog.clientBilling}
                  onChange={(e) => setDialog((d) => ({ ...d, clientBilling: e.target.value }))}
                  fullWidth
                  required
                />
              )}
              {dialog.billingMode === 'lumpsum' && (
                <TextField
                  label="Margin benefit %"
                  type="number"
                  value={dialog.margin}
                  onChange={(e) => setDialog((d) => ({ ...d, margin: e.target.value }))}
                  fullWidth
                  required
                />
              )}
              {dialog.billingMode === 'billed' && (
                <TextField
                  label="Margin benefit % (auto-calculated)"
                  value={billedMarginPreview != null ? `${billedMarginPreview}%` : '—'}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  helperText="Computed from (1 − total cost ÷ client billing) × 100"
                />
              )}
            </Stack>
          )}

          {dialog.action === 'revised' && (
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <TextField
                select
                label="Send back to"
                value={dialog.revisionTarget}
                onChange={(e) => setDialog((d) => ({ ...d, revisionTarget: e.target.value }))}
                fullWidth
                required
              >
                {revisionTargetOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Revision reason"
                value={dialog.reason}
                onChange={(e) => setDialog((d) => ({ ...d, reason: e.target.value }))}
                fullWidth
                required
                multiline
                minRows={2}
              />
            </Stack>
          )}

          {dialog.action === 'rejected' && (
            <TextField
              label="Rejection reason"
              value={dialog.reason}
              onChange={(e) => setDialog((d) => ({ ...d, reason: e.target.value }))}
              fullWidth
              required
              multiline
              minRows={2}
              sx={{ mt: 0.5 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={submitDialog}
            disabled={submitting}
            variant="contained"
            color={dialog.action === 'approved' ? 'success' : dialog.action === 'rejected' ? 'error' : 'warning'}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {dialog.action === 'approved' ? 'Approve' : dialog.action === 'rejected' ? 'Reject' : 'Request revision'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
